import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getTableCellRectByKey } from '../../domains/diagram/shapes/table/tableDrop.utils';
import { resolveCellLayoutConfig } from '../../domains/diagram/shapes/table/tableCellLayout.registry';
import { computeDockedLayoutUpdatesForTable } from '../../domains/diagram/shapes/table/tableDockLayout.utils';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };
type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const MIN_CELL = 20;
const DEFAULT_HEADER = 32;

const clampCount = (value: unknown, fallback: number, min = 1): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

const ensureArray = (arr: unknown, count: number, fallbackTotal: number): number[] => {
  if (Array.isArray(arr) && arr.length === count && arr.every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) {
    return arr as number[];
  }
  const even = Math.max(MIN_CELL, fallbackTotal / Math.max(1, count));
  return Array(count).fill(even);
};

const parseKey = (k: `${number},${number}`) => {
  const [rStr, cStr] = String(k).split(',');
  return { r: Number(rStr), c: Number(cStr) };
};

const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

export class AutoResizeTableCellToFitDockedContentCommand extends BaseNodeCommand {
  constructor(private tableId: string, private cellKey: `${number},${number}`) {
    super('Auto Resize Table Cell', `Resize cell ${cellKey} to fit docked content`);
  }

  protected doExecute(): NodeChangeRecord[] {
    const mm: any = this.getMutationManager();
    const store = useDiagramStore.getState();
    const tableNode = store.nodes.find((n) => n.id === this.tableId) as any;
    if (!tableNode || tableNode.type !== 'table') return [];

    const hit = getTableCellRectByKey(tableNode, this.cellKey);
    if (!hit) return [];

    const attrs = (tableNode.attrs ?? {}) as Record<string, unknown>;
    const rowCount = clampCount(attrs.rowCount, 3);
    const colCount = clampCount(attrs.columnCount, 3);
    const showHeader = attrs.showHeader !== false;
    const headerHeight =
      typeof attrs.headerHeight === 'number' && Number.isFinite(attrs.headerHeight) && attrs.headerHeight > 0
        ? Math.max(MIN_CELL, attrs.headerHeight)
        : DEFAULT_HEADER;

    const width0 = ((tableNode.width ?? tableNode.w ?? 240) as number) || 240;
    const height0 = ((tableNode.height ?? tableNode.h ?? 180) as number) || 180;
    const bodyHeight = Math.max(MIN_CELL * rowCount, height0 - (showHeader ? headerHeight : 0));

    const rowHeights = ensureArray((attrs as any).rowHeights, rowCount, bodyHeight);
    const colWidths = ensureArray((attrs as any).columnWidths, colCount, width0);

    const mergedRegions = (Array.isArray((attrs as any).mergedRegions) ? ((attrs as any).mergedRegions as MergedRegion[]) : []) ?? [];
    const anchor = parseKey(hit.cellKey);
    const mr = mergedRegions.find((m) => containsCell(m, anchor.r, anchor.c)) ?? null;
    const targetRow = mr ? mr.endRow : anchor.r;
    const targetCol = mr ? mr.endCol : anchor.c;

    const docked = store.nodes.filter((n) => {
      const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
      if (!dock) return false;
      if (dock.tableId !== this.tableId) return false;
      if (dock.cellKey === hit.cellKey) return true;
      if (!mr) return false;
      const p = parseKey(dock.cellKey);
      return Number.isFinite(p.r) && Number.isFinite(p.c) && containsCell(mr, p.r, p.c);
    }) as any[];

    if (docked.length === 0) return [];

    const config = resolveCellLayoutConfig(attrs, hit.cellKey as any);
    const gap = typeof (config.params as any)?.gap === 'number' ? Math.max(0, (config.params as any).gap) : 8;
    const padding = typeof (config.params as any)?.padding === 'number' ? Math.max(0, (config.params as any).padding) : 8;
    const layoutRect = hit.contentRect;

    const items = docked
      .slice()
      .sort((a: any, b: any) => {
        const za = Number((a as any).zIndex ?? 0);
        const zb = Number((b as any).zIndex ?? 0);
        if (za !== zb) return za - zb;
        return String(a.id).localeCompare(String(b.id));
      })
      .map((n: any) => {
        const w = (n.w ?? n.width ?? 60) as number;
        const h = (n.h ?? n.height ?? 60) as number;
        const dock = (n as any)?.attrs?.tableDock as TableDock | undefined;
        const offX =
          typeof dock?.offset?.x === 'number' && Number.isFinite(dock.offset.x) ? dock.offset.x : (Number(n.x) || 0) - layoutRect.x;
        const offY =
          typeof dock?.offset?.y === 'number' && Number.isFinite(dock.offset.y) ? dock.offset.y : (Number(n.y) || 0) - layoutRect.y;
        return {
          w: Number(w) || 60,
          h: Number(h) || 60,
          offX,
          offY,
        };
      });

    let requiredW = layoutRect.w;
    let requiredH = layoutRect.h;

    if (config.algorithmId === 'fixed') {
      let maxX = 0;
      let maxY = 0;
      for (const it of items) {
        maxX = Math.max(maxX, Math.max(0, it.offX) + it.w);
        maxY = Math.max(maxY, Math.max(0, it.offY) + it.h);
      }
      requiredW = Math.max(requiredW, maxX);
      requiredH = Math.max(requiredH, maxY);
    } else {
      const maxW = Math.max(...items.map((i) => i.w), 60);
      const maxH = Math.max(...items.map((i) => i.h), 60);
      const availableW = Math.max(1, layoutRect.w - padding * 2);
      const autoCols = Math.max(1, Math.floor((availableW + gap) / (maxW + gap)));
      const cols = autoCols;
      const rows = Math.max(1, Math.ceil(items.length / cols));

      const needW = padding * 2 + cols * maxW + (cols - 1) * gap;
      const needH = padding * 2 + rows * maxH + (rows - 1) * gap;
      requiredW = Math.max(requiredW, needW);
      requiredH = Math.max(requiredH, needH);
    }

    const deltaW = Math.max(0, requiredW - layoutRect.w);
    const deltaH = Math.max(0, requiredH - layoutRect.h);
    if (deltaW === 0 && deltaH === 0) return [];

    const nextRowHeights = [...rowHeights];
    const nextColWidths = [...colWidths];
    let nextHeaderHeight = headerHeight;

    if (deltaW > 0) {
      const idx = Math.max(0, Math.min(colCount - 1, targetCol));
      nextColWidths[idx] = Math.max(MIN_CELL, (nextColWidths[idx] ?? MIN_CELL) + deltaW);
    }

    if (deltaH > 0) {
      const rIdx = Math.max(0, targetRow);
      if (showHeader && rIdx === 0) {
        nextHeaderHeight = Math.max(MIN_CELL, nextHeaderHeight + deltaH);
      } else {
        const bodyIdx = showHeader ? rIdx - 1 : rIdx;
        if (bodyIdx >= 0 && bodyIdx < nextRowHeights.length) {
          nextRowHeights[bodyIdx] = Math.max(MIN_CELL, (nextRowHeights[bodyIdx] ?? MIN_CELL) + deltaH);
        }
      }
    }

    const nextWidth = width0 + deltaW;
    const nextHeight = height0 + deltaH;

    const tableChanges: any = {
      width: nextWidth,
      w: nextWidth,
      height: nextHeight,
      h: nextHeight,
      attrs: {
        columnWidths: nextColWidths,
        rowHeights: nextRowHeights,
        ...(showHeader ? { headerHeight: nextHeaderHeight } : {}),
      },
    };

    const previewAttrs = { ...(attrs as any), ...(tableChanges.attrs as any) };
    const previewTable: any = { ...tableNode, ...tableChanges, attrs: previewAttrs };
    const dockedUpdates = computeDockedLayoutUpdatesForTable(previewTable, store.nodes);

    const batch = [{ nodeId: this.tableId, changes: tableChanges }, ...dockedUpdates];
    if (batch.length > 1 && typeof mm.applyBatchNodeChanges === 'function') {
      return mm.applyBatchNodeChanges(batch) ?? [];
    }
    const rec = mm.applyNodeChange(this.tableId, tableChanges);
    return rec ? [rec] : [];
  }
}
